import { createContext, useContext, useState, useRef, useEffect, useCallback } from "react";
import { useNotification } from "../../../providers/NotificationProvider";
import { useEntities } from "@emberly/zenith-client";
import { getLocalStorageValue } from "../../../common/helpers";
import OperationsMap from "./map/OperationsMap";
import { useTranslation } from "react-i18next";
import { useDispatch as useReduxDispatch, useSelector } from "react-redux";
import { MAP_LAYERS_OPEN, MAP_TASKS_OPEN } from "../../../store/actions";
import { DRIVERS_QUERY } from "../../../common/queries";
import { useDispatch } from "../dispatch/providers/DispatchProvider";
import { useVehiclesWithFleetMgmt } from "../../../hooks/useVehiclesWithFleetMgmt";

export const OperationsMapContext = createContext();

export const useOperationsMap = () => useContext(OperationsMapContext);

export const OperationsMapProvider = (props) => {
  const settingsRef = useRef(OperationsMap.ReadSettings("main"));
  const settings = settingsRef.current;
  const { t } = useTranslation();

  const dispatch = useReduxDispatch();
  const { mapLayersOpen: topLeftExpanded, mapTasksOpen: topRightExpanded } = useSelector(t => t.app);

  const setTopLeftExpanded = useCallback((val) => dispatch({ type: MAP_LAYERS_OPEN, payload: val }), [dispatch]);
  const setTopRightExpanded = useCallback((val) => dispatch({ type: MAP_TASKS_OPEN, payload: val }), [dispatch]);

  const [styleSelectorExpanded, setStyleSelectorExpanded] = useState(false);
  const [style, setStyle] = useState(settings.styleId || "standard");
  const [operationsMap, setOperationsMap] = useState(null);
  const [dispatchLayerHoverId, setDispatchLayerHoverId] = useState(null);
  const [vehicleLayerHoverId, setVehicleLayerHoverId] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const [showTags, setShowTags] = useState(!!settings?.plugins?.dispatch?.showTags);
  const [showCause, setShowCause] = useState(!!settings?.plugins?.dispatch?.showCause);
  const [showMissionType, setShowMissionType] = useState(!!settings?.plugins?.dispatch?.showMissionType);

  const [taskId, setTaskId] = useState(null);
  const [vehicleId, setVehicleId] = useState(null);
  const { snackbar } = useNotification();

  const containerRef = useRef(null);

  const resetSettings = useCallback(() => {
    operationsMap?.resetSettings();
    setShowTags(true);
    setShowCause(true);
    setShowMissionType(false);
    snackbar(t("operationsMap:resetSettingsSnackbar"));
  }, [operationsMap, snackbar, t]);

  const { activeTasks, completedTasks, initialLoad, filteredActiveTasks: filteredTasks } = useDispatch();
  const { entities: drivers } = useEntities("Employee", DRIVERS_QUERY);
  const vehicles = useVehiclesWithFleetMgmt(activeTasks);

  useEffect(() => {
    operationsMap?.updateDispatchData(filteredTasks, completedTasks);
  }, [filteredTasks, completedTasks, operationsMap]);

  useEffect(() => {
    operationsMap?.updateVehicleData(vehicles);
  }, [vehicles, operationsMap]);


  const openTaskDialog = useCallback((taskId) => {
    setTaskId(taskId);
  }, []);

  const closeTaskDialog = useCallback(() => {
    setTaskId(null);
  }, []);


  const openVehicleDialog = useCallback((vehicleId) => {
    setVehicleId(vehicleId);
  }, []);

  const closeVehicleDialog = useCallback(() => {
    setVehicleId(null);
  }, []);

  return (
    <OperationsMapContext.Provider
      value={{
        topLeftExpanded,
        setTopLeftExpanded,
        topRightExpanded,
        setTopRightExpanded,
        styleSelectorExpanded,
        setStyleSelectorExpanded,
        dispatchLayerHoverId,
        setDispatchLayerHoverId,
        style,
        setStyle,
        drivers,
        containerRef,
        filteredTasks,
        activeTasks,
        completedTasks,
        operationsMap,
        setOperationsMap,
        initialLoad,

        openTaskDialog,
        closeTaskDialog,
        taskId,
        settings,
        resetSettings,
        loaded,
        setLoaded,
        vehicles,
        vehicleId,
        openVehicleDialog,
        closeVehicleDialog,
        setVehicleLayerHoverId,
        vehicleLayerHoverId,
        showTags, 
        setShowTags,
        showCause, 
        setShowCause,
        showMissionType, 
        setShowMissionType,
      }}
    >
      {props.children}
    </OperationsMapContext.Provider>
  );
};

